import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Alert, Button, Card, Form, Input } from 'antd'
import { signInWithRedirect } from 'aws-amplify/auth'
import { xeroSignupEmail } from '../api'
import { isAmplifyConfigured } from '@/lib/amplify'
import { formatPhone, normalizePhone } from '@/lib/phone'
import { setSession } from '@/lib/session'
import AuthLogo from '../components/AuthLogo'
import { getCurrentCognitoSession } from '../amplifyAuth'

export default function SignupXeroPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasUser, setHasUser] = useState(false)
  const [form] = Form.useForm<{ firstName: string; lastName: string; email: string; phone: string }>()

  useEffect(() => {
    if (!isAmplifyConfigured()) {
      setLoading(false)
      return
    }
    getCurrentCognitoSession()
      .then((user) => {
        setHasUser(true)
        const parts = user.name ? user.name.trim().split(/\s+/, 2) : ['', '']
        form.setFieldsValue({
          firstName: parts[0] || '',
          lastName: parts[1] || '',
          email: user.email,
          phone: '',
        })
        const expiry = new Date(Date.now() + user.expirySeconds * 1000).toISOString()
        setSession({
          email: user.email,
          idToken: user.idToken,
          refreshToken: user.refreshToken,
          tokenExpiry: expiry,
          currentClientId: '',
        })
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [form])

  const onFinish = async (values: { firstName: string; lastName: string; email: string; phone: string }) => {
    if (!isAmplifyConfigured()) return
    setError(null)
    setSubmitting(true)
    try {
      const user = await getCurrentCognitoSession()
      const xeroUserID = user.username
      const normalizedPhone = normalizePhone(values.phone)
      await xeroSignupEmail(
        values.firstName,
        values.lastName,
        values.email,
        normalizedPhone || 'Not recorded',
        xeroUserID
      )
      navigate('/login', { replace: true })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Signup failed')
    } finally {
      setSubmitting(false)
    }
  }

  const onSignUpWithXero = () => {
    if (!isAmplifyConfigured()) return
    signInWithRedirect({ provider: { custom: 'Xero' } }).catch((e: unknown) => setError(e instanceof Error ? e.message : 'Xero sign-in failed'))
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#d1e8e8] flex items-center justify-center">
        <div className="text-gray-700">Loading...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#d1e8e8] flex flex-col items-center justify-center p-6">
      <AuthLogo className="h-14 w-auto mb-6" />
      <Card className="w-full max-w-md" title={hasUser ? 'Complete your Xero signup' : 'Sign up with Xero'}>
        {error && <Alert type="error" message={error} className="mb-4" />}
        {!hasUser ? (
          <>
            <p className="text-gray-600 mb-4">Use your Xero account to sign up for Dashboard Insights.</p>
            <Button
              type="primary"
              size="large"
              block
              onClick={onSignUpWithXero}
              icon={<img src="/assets/media/svg/icons/Xero.svg" alt="" className="h-5 w-5 mr-2" />}
            >
              Sign up with Xero
            </Button>
            <p className="text-center text-sm text-gray-500 mt-4">
              <Link to="/login">Already have an account? Sign in</Link>
            </p>
          </>
        ) : (
          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item name="firstName" label="First name" rules={[{ required: true, min: 3, max: 100 }]}>
              <Input size="large" />
            </Form.Item>
            <Form.Item name="lastName" label="Last name" rules={[{ required: true, min: 3, max: 100 }]}>
              <Input size="large" />
            </Form.Item>
            <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
              <Input size="large" />
            </Form.Item>
            <Form.Item
              name="phone"
              label="Phone"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve()
                    return normalizePhone(value).length === 10
                      ? Promise.resolve()
                      : Promise.reject(new Error('Phone number must be exactly 10 digits.'))
                  },
                },
              ]}
              getValueFromEvent={(event) => normalizePhone(event?.target?.value).slice(0, 10)}
              getValueProps={(value) => ({ value: formatPhone(value) === '—' ? '' : formatPhone(value) })}
            >
              <Input size="large" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting} block size="large">
                Complete signup
              </Button>
            </Form.Item>
          </Form>
        )}
      </Card>
    </div>
  )
}
